import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Badge,
  Drawer,
  TextField,
  Grid,
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { Comment } from "@mui/icons-material";
import { basicUTCFormat } from "../../extends/NumberFormat";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const styles = (theme) => ({
  typography: {
    padding: 10,
    maxWidth: 400,
    maxHeight: 400,
    overflowY: "auto",
  },
  margin: {
    marginRight: 10,
  },
  border: {
    border: "1px solid #F6F6F9",
    borderRadius: 10,
    background: "#F6F6F9",
    padding: 13,
    marginBottom: 20,
  },
  date: {
    color: "gray",
    fontSize: 12,
  },
  icon: {
    height: 20,
    cursor: "pointer",
    width: 20,
  },
  colorUnderline: {
    borderBottom: `1px solid #0091ff`,
    "&:after": {
      borderBottom: `1px solid #0091ff`,
    },
  },
});

const PopoverComment = ({
  classes,
  type,
  comments = [],
  onAddComment,
  onOpen,
}) => {
  const [comment, setComment] = useState("");
  const hasComment = comments?.length > 0;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const useIsWidthDown = (breakpoint, width) => {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down(breakpoint));
  };
  const chatLogBoxRef = useRef(null);

  const submitForm = async () => {
    setIsSending(true);
    const replyComment = {
      ...type,
      id: -1,
      isCatererReply: false,
      isArchived: false,
      parentThreadId:
        type?.id === -1
          ? type?.parentThreadId
          : type?.parentThreadId || type?.threadId,
      content: comment,
    };
    if (onAddComment) {
      await onAddComment(replyComment);
      setComment("");
      setIsSending(false);
    }
  };

  useEffect(() => {
    onOpen && onOpen(drawerOpen);
    if (!drawerOpen) {
      return;
    }
    if (!chatLogBoxRef.current) {
      return;
    }
    chatLogBoxRef.current.scrollTop = chatLogBoxRef.current.scrollHeight;
  }, [drawerOpen, onOpen, comments]);

  const sendDisabled = isSending || !(comment ?? "").trim()?.length;
  return (
    <Grid item className={classes.margin}>
      <ButtonGroup>
        <Tooltip title="Leave a comment">
          <IconButton
            color={hasComment ? "primary" : ""}
            onClick={(event) => {
              setDrawerOpen(true);
            }}
          >
            <Badge
              badgeContent={
                comments.filter((c) => !c?.isArchived && c.isCatererReply)
                  .length || 0
              }
              color="secondary"
            >
              <Comment></Comment>
            </Badge>
          </IconButton>
        </Tooltip>
      </ButtonGroup>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setIsSending(false);
        }}
        disablePortal
      >
        <Stack
          sx={{
            width: useIsWidthDown("sm", 300) ? "80vw" : 500,
            height: "100%",
          }}
        >
          <Alert severity="info">Comment History</Alert>
          <Stack ref={chatLogBoxRef} sx={{ height: "80%", overflow: "auto" }}>
            <List dense sx={{ m: 0, p: 0 }}>
              {comments.map((c, i) => (
                <ListItem
                  key={c?.id}
                  sx={{
                    gap: 1,
                    alignItems: "flex-start",
                    flexDirection: !c.isCatererReply ? "row-reverse" : "",
                  }}
                >
                  {c.isCatererReply && (
                    <ListItemAvatar sx={{ minWidth: 30 }}>
                      <Avatar></Avatar>
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    sx={{ textAlign: !c.isCatererReply ? "right" : "" }}
                    primary={
                      <Box
                        sx={{
                          whiteSpace: "pre-wrap",
                          maxHeight: 150,
                          overflow: "auto",
                          borderRadius: 2,
                          color: !c.isCatererReply ? "#fff" : null,
                          bgcolor: !c.isCatererReply ? "info.light" : "#eee",
                          p: 1,
                        }}
                      >
                        {c?.content}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption">
                        {basicUTCFormat(c?.recAddedDate)}
                      </Typography>
                    }
                  ></ListItemText>
                </ListItem>
              ))}
            </List>
          </Stack>

          <Stack sx={{ p: 1 }}>
            <TextField
              id="filled-multiline-static"
              label="Comment"
              multiline
              rows="4"
              autoFocus
              InputProps={{ classes: { underline: classes.colorUnderline } }}
              onChange={(event) => {
                setComment(event.target.value);
              }}
              value={comment}
              fullWidth
              margin="normal"
              variant="filled"
            />
            <Button
              variant="contained"
              fullWidth
              onClick={submitForm}
              disabled={sendDisabled}
            >
              Send Reply
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </Grid>
  );
};

PopoverComment.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PopoverComment);
